package cz.lhoracek.mapl.ui.screens.addround

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import cz.lhoracek.mapl.data.model.FLEK_NAMES
import cz.lhoracek.mapl.data.model.GameType
import cz.lhoracek.mapl.data.model.Player
import cz.lhoracek.mapl.data.model.Session
import cz.lhoracek.mapl.data.model.SessionWithPlayers
import cz.lhoracek.mapl.data.model.Suit
import cz.lhoracek.mapl.ui.getInitials
import cz.lhoracek.mapl.ui.hexToColor
import cz.lhoracek.mapl.ui.components.MaplTopAppBar
import cz.lhoracek.mapl.ui.components.NumberStepper
import cz.lhoracek.mapl.ui.theme.MaplTheme
import cz.lhoracek.mapl.ui.components.PlayerAvatar as CommonPlayerAvatar
import mapl.composeapp.generated.resources.*
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun AddRoundScreen(
    sessionId: String,
    onBack: () -> Unit,
) {
    val viewModel: AddRoundViewModel = koinViewModel(
        key = "addround_$sessionId",
        parameters = { parametersOf(sessionId) },
    )
    val state by viewModel.uiState.collectAsState()
    val sessionData by viewModel.session.collectAsState()

    LaunchedEffect(state.savedOk) {
        if (state.savedOk) {
            viewModel.consumeNavigation()
            onBack()
        }
    }

    AddRoundContent(
        state = state,
        sessionData = sessionData,
        onBack = onBack,
        onActorClick = viewModel::setActor,
        onGameTypeClick = viewModel::setGameType,
        onSuitClick = viewModel::setSuit,
        onFlekLevelChange = viewModel::setFlekLevel,
        onNoCallerClick = viewModel::confirmNoCaller,
        onWonClick = { won ->
            viewModel.setWon(won)
            viewModel.confirm()
        },
        previewDeltas = viewModel::previewDeltas,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun AddRoundContent(
    state: AddRoundUiState,
    sessionData: SessionWithPlayers?,
    onBack: () -> Unit,
    onActorClick: (String) -> Unit,
    onGameTypeClick: (GameType) -> Unit,
    onSuitClick: (Suit) -> Unit,
    onFlekLevelChange: (Int) -> Unit,
    onNoCallerClick: () -> Unit,
    onWonClick: (Boolean) -> Unit,
    previewDeltas: (List<Player>, Int, Boolean) -> Map<String, Int>,
) {
    Scaffold(
        topBar = {
            MaplTopAppBar(
                title = stringResource(Res.string.add_round_title),
                onBack = onBack,
            )
        },
    ) { innerPadding ->
        val players = sessionData?.players ?: return@Scaffold
        val currentBet = sessionData.session.currentBet
        val flekName = FLEK_NAMES.getOrElse(state.flekLevel) { "x${1 shl state.flekLevel}" }

        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding),
        ) {
            val contentWidth = maxWidth.coerceAtMost(560.dp)

            Column(
                modifier = Modifier
                    .width(contentWidth)
                    .align(Alignment.TopCenter)
                    .padding(horizontal = 12.dp, vertical = 8.dp)
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
            // ── Hráč ──────────────────────────────────────────────────────
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    stringResource(Res.string.add_round_playing),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.width(56.dp)
                )
                players.forEach { player ->
                    PlayerAvatar(
                        name = player.name,
                        color = player.color,
                        selected = state.actorId == player.id,
                        onClick = { onActorClick(player.id) },
                        modifier = Modifier.weight(1f),
                    )
                }
            }

            HorizontalDivider()

            if (sessionData.session.potEnabled) {
                FilledTonalButton(
                    onClick = onNoCallerClick,
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !state.isSaving,
                ) {
                    Text(
                        stringResource(
                            Res.string.add_round_no_caller_to_pot,
                            sessionData.session.currentBet * 2,
                        )
                    )
                }
                HorizontalDivider()
            }

            // ── Co se hraje ────────────────────────────────────────────────
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    stringResource(Res.string.add_round_game),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.width(56.dp)
                )
                GameType.entries.forEach { gameType ->
                    val selected = state.gameType == gameType
                    Surface(
                        modifier = Modifier
                            .weight(1f)
                            .aspectRatio(1f)
                            .clickable { onGameTypeClick(gameType) },
                        shape = MaterialTheme.shapes.medium,
                        color = if (selected) MaterialTheme.colorScheme.primaryContainer
                        else MaterialTheme.colorScheme.surfaceVariant,
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Text(
                                    gameType.label,
                                    style = MaterialTheme.typography.titleSmall,
                                    fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                                    color = if (selected) MaterialTheme.colorScheme.primary
                                    else MaterialTheme.colorScheme.onSurfaceVariant,
                                )
                                Text(
                                    "x${gameType.multiplier}",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = if (selected) MaterialTheme.colorScheme.primary
                                    else MaterialTheme.colorScheme.onSurfaceVariant,
                                )
                            }
                        }
                    }
                }
            }

            HorizontalDivider()

            // ── Barva ──────────────────────────────────────────────────────
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    stringResource(Res.string.add_round_suit),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.width(56.dp)
                )
                Suit.entries.forEach { suit ->
                    val selected = state.suit == suit
                    Surface(
                        modifier = Modifier
                            .weight(1f)
                            .aspectRatio(1f)
                            .height(56.dp)
                            .clickable { onSuitClick(suit) },
                        shape = MaterialTheme.shapes.medium,
                        color = when {
                            selected && suit == Suit.LEPSI -> MaterialTheme.colorScheme.tertiaryContainer
                            selected -> MaterialTheme.colorScheme.primaryContainer
                            else -> MaterialTheme.colorScheme.surfaceVariant
                        },
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Image(
                                painter = painterResource(
                                    when (suit) {
                                        Suit.ZALUDY -> Res.drawable.suit_zaludy
                                        Suit.KULE -> Res.drawable.suit_kule
                                        Suit.ZELENE -> Res.drawable.suit_zelene
                                        Suit.LEPSI -> Res.drawable.suit_lepsi
                                    }
                                ),
                                contentDescription = suit.label,
                                modifier = Modifier.size(40.dp),
                            )
                        }
                    }
                }
            }

            HorizontalDivider()

            // ── Flek ───────────────────────────────────────────────────────
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    stringResource(Res.string.add_round_flek),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.width(56.dp)
                )
                NumberStepper(
                    valueLabel = "$flekName  x${1 shl state.flekLevel}",
                    onDecrement = { onFlekLevelChange(state.flekLevel - 1) },
                    onIncrement = { onFlekLevelChange(state.flekLevel + 1) },
                    decrementEnabled = state.flekLevel > 0,
                    incrementEnabled = state.flekLevel < 6,
                    modifier = Modifier.weight(1f),
                    valueStyle = MaterialTheme.typography.bodyLarge,
                    valueContent = {
                        Text(
                            text = "$flekName  x${1 shl state.flekLevel}",
                            style = MaterialTheme.typography.bodyLarge,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary,
                            modifier = Modifier.weight(1f),
                            textAlign = TextAlign.Center,
                        )
                    },
                )
            }

            HorizontalDivider()

            // ── Výsledek kola ──────────────────────────────────────────────
            if (state.actorId.isNotBlank()) {
                val deltasWon = previewDeltas(players, currentBet, true)
                val deltasLost = previewDeltas(players, currentBet, false)

                Surface(
                    shape = MaterialTheme.shapes.medium,
                    color = MaterialTheme.colorScheme.surfaceVariant,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Column(
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        Row(modifier = Modifier.fillMaxWidth()) {
                            Spacer(Modifier.weight(1f))
                            Text(
                                stringResource(Res.string.session_round_won),
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.weight(1f),
                                textAlign = TextAlign.End,
                            )
                            Text(
                                stringResource(Res.string.session_round_lost),
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.error,
                                modifier = Modifier.weight(1f),
                                textAlign = TextAlign.End,
                            )
                        }
                        players.forEach { player ->
                            val dWon = deltasWon[player.id] ?: 0
                            val dLost = deltasLost[player.id] ?: 0
                            Row(modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    player.name,
                                    style = MaterialTheme.typography.bodyMedium,
                                    modifier = Modifier.weight(1f),
                                )
                                Text(
                                    text = if (dWon >= 0) "+$dWon" else "$dWon",
                                    fontWeight = FontWeight.Bold,
                                    color = if (dWon >= 0) MaterialTheme.colorScheme.primary
                                    else MaterialTheme.colorScheme.error,
                                    modifier = Modifier.weight(1f),
                                    textAlign = TextAlign.End,
                                )
                                Text(
                                    text = if (dLost >= 0) "+$dLost" else "$dLost",
                                    fontWeight = FontWeight.Bold,
                                    color = if (dLost >= 0) MaterialTheme.colorScheme.primary
                                    else MaterialTheme.colorScheme.error,
                                    modifier = Modifier.weight(1f),
                                    textAlign = TextAlign.End,
                                )
                            }
                        }
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    Button(
                        onClick = { onWonClick(true) },
                        modifier = Modifier.weight(1f).height(56.dp),
                        enabled = !state.isSaving,
                    ) {
                        if (state.isSaving && state.won) {
                            CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                        } else {
                            Text(stringResource(Res.string.session_round_won), style = MaterialTheme.typography.titleMedium)
                        }
                    }
                    Button(
                        onClick = { onWonClick(false) },
                        modifier = Modifier.weight(1f).height(56.dp),
                        enabled = !state.isSaving,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.error,
                        ),
                    ) {
                        if (state.isSaving && !state.won) {
                            CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                        } else {
                            Text(stringResource(Res.string.session_round_lost), style = MaterialTheme.typography.titleMedium)
                        }
                    }
                }
            }
        }
    }
}
}

@Composable
private fun PlayerAvatar(
    name: String,
    color: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val playerColor = hexToColor(color)
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp),
        modifier = modifier
            .clip(MaterialTheme.shapes.medium)
            .background(
                if (selected) playerColor.copy(alpha = 0.15f)
                else Color.Transparent
            )
            .clickable(onClick = onClick)
            .padding(vertical = 8.dp),
    ) {
        CommonPlayerAvatar(
            name = name,
            colorHex = color,
            size = 56.dp,
            textStyle = MaterialTheme.typography.titleLarge
        )
        Text(
            text = name,
            style = MaterialTheme.typography.labelMedium,
            color = if (selected) playerColor else MaterialTheme.colorScheme.onSurfaceVariant,
            fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
            textAlign = TextAlign.Center,
            maxLines = 1,
        )
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(
        text,
        style = MaterialTheme.typography.titleSmall,
        fontWeight = FontWeight.Bold,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

@Preview
@Composable
fun AddRoundScreenPreview() {
    val players = listOf(
        Player("1", "Hráč 1", "#E53935"),
        Player("2", "Hráč 2", "#43A047"),
        Player("3", "Hráč 3", "#1E88E5")
    )
    val session = Session("s1", "Session 1", 1, 1)
    val sessionWithPlayers = SessionWithPlayers(session, players)

    MaplTheme {
        AddRoundContent(
            state = AddRoundUiState(actorId = "1"),
            sessionData = sessionWithPlayers,
            onBack = {},
            onActorClick = {},
            onGameTypeClick = {},
            onSuitClick = {},
            onFlekLevelChange = {},
            onNoCallerClick = {},
            onWonClick = {},
            previewDeltas = { _, _, won ->
                if (won) {
                    mapOf("1" to 4, "2" to -2, "3" to -2)
                } else {
                    mapOf("1" to -4, "2" to 2, "3" to 2)
                }
            }
        )
    }
}
